import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';

// Timing instrumentation for diagnosing slow Listen page loads.
// Logs how long each parallel branch of the initial fetch takes.
const pageOpenedAt = performance.now();
function timed(label, promise) {
  const start = performance.now();
  return Promise.resolve(promise).then(
    result => {
      console.log(`⏱️ Listen load: ${label} took ${Math.round(performance.now() - start)}ms`);
      return result;
    },
    error => {
      console.log(`⏱️ Listen load: ${label} FAILED after ${Math.round(performance.now() - start)}ms`, error);
      throw error;
    }
  );
}

// Optimized data fetching hook that consolidates all Firestore queries
export function useListenDataOptimized(currentUser, userFamilyIds) {
  const [data, setData] = useState({
    userFamilies: [],
    availableListeningPlans: [],
    currentListeningPlan: null,
    availablePassagePlans: [],
    availableReaders: [],
    recordedPassages: [],
    matchingRecordings: [],
    allRecordings: [],
    verseNotes: {},
    hasNotesForChapter: false,
    hasSearchedForRecordings: false,
    favoritedVerses: new Set(),
    loading: true,
    error: null,
  });

  // Memoized family member IDs fetcher — family docs fetched in parallel
  const getFamilyMemberIds = useCallback(async (familyIds) => {
    const allMemberIds = new Set();

    await Promise.all(familyIds.map(async (familyId) => {
      try {
        const familyDoc = await getDoc(doc(db, 'families', familyId));
        if (familyDoc.exists()) {
          const familyData = familyDoc.data();
          const memberIds = familyData.memberIds || familyData.memberIDs;
          if (memberIds && memberIds.length > 0) {
            memberIds.forEach(id => allMemberIds.add(id));
          }
        }
      } catch (error) {
        console.error(`Error fetching family ${familyId}:`, error);
      }
    }));

    return Array.from(allMemberIds);
  }, []);

  // Optimized recordings fetcher with proper filtering
  const fetchRecordings = useCallback(async (filters = {}) => {
    if (!currentUser || userFamilyIds.length === 0) {
      return { recordedPassages: [], matchingRecordings: [] };
    }

    try {
      const { selectedFamily = 'all', selectedReader = 'all', selectedPlan = 'all', availableListeningPlans = [] } = filters;

      // Get family member IDs
      const memberIdsStart = performance.now();
      const familyMemberIds = await getFamilyMemberIds(userFamilyIds);
      console.log(`⏱️ Listen load: recordings — resolved ${familyMemberIds.length} member ids in ${Math.round(performance.now() - memberIdsStart)}ms`);

      // Build optimized queries
      const queries = [];

      // User's own recordings
      queries.push(
        query(collection(db, 'recordings'), where('userId', '==', currentUser.uid))
      );

      // Family member recordings (batched by 10)
      for (let i = 0; i < familyMemberIds.length; i += 10) {
        const batch = familyMemberIds.slice(i, i + 10);
        queries.push(
          query(collection(db, 'recordings'), where('userId', 'in', batch))
        );
      }

      // Execute all queries in parallel
      const queriesStart = performance.now();
      const snapshots = await Promise.all(queries.map(q => getDocs(q)));
      console.log(`⏱️ Listen load: recordings — ${queries.length} Firestore queries returned ${snapshots.reduce((n, s) => n + s.docs.length, 0)} docs in ${Math.round(performance.now() - queriesStart)}ms`);
      
      // Combine and process results
      // The "own recordings" and "family member" queries overlap (the current
      // user is also a family member), so de-duplicate by document id.
      const recordingsById = new Map();
      snapshots.forEach(snapshot =>
        snapshot.docs.forEach(doc => recordingsById.set(doc.id, { id: doc.id, ...doc.data() }))
      );
      let allRecordings = [...recordingsById.values()];

      console.log('🎵 Raw recordings fetched:', {
        totalQueries: queries.length,
        totalRecordings: allRecordings.length,
        sampleRecordings: allRecordings.slice(0, 3).map(r => ({
          id: r.id,
          book: r.book,
          chapter: r.chapter,
          verse: r.verse,
          hasAudioURL: !!r.audioURL,
          userId: r.userId
        }))
      });
      
      // Apply filters
      let filteredRecordings = allRecordings;
      
      if (selectedFamily !== 'all') {
        filteredRecordings = filteredRecordings.filter(recording => 
          recording.familyIds && recording.familyIds.includes(selectedFamily)
        );
      }
      
      if (selectedReader !== 'all') {
        filteredRecordings = filteredRecordings.filter(recording => 
          recording.userId === selectedReader
        );
      }
      
      if (selectedPlan !== 'all') {
        const selectedPlanData = availableListeningPlans.find(p => p.id === selectedPlan);
        if (selectedPlanData && selectedPlanData.chapters) {
          filteredRecordings = filteredRecordings.filter(recording => {
            return selectedPlanData.chapters.some(chapter => 
              chapter.book === recording.book && chapter.chapter === recording.chapter
            );
          });
        }
      }
      
        // Process into passages
        const passages = new Set();
        const passageData = [];
        
        filteredRecordings.forEach(recording => {
          const passageStr = `${recording.book} ${recording.chapter}:${recording.verse}`;
          if (!passages.has(passageStr)) {
            passages.add(passageStr);
            passageData.push({
              passage: passageStr,
              book: recording.book,
              chapter: recording.chapter,
              verse: recording.verse,
            });
          }
        });
        
        return {
          recordedPassages: passageData,
          matchingRecordings: filteredRecordings,
          allRecordings: filteredRecordings
        };
      
    } catch (error) {
      console.error('Error fetching recordings:', error);
      return { recordedPassages: [], matchingRecordings: [], allRecordings: [] };
    }
  }, [currentUser, userFamilyIds, getFamilyMemberIds]);

  // Fetch matching recordings for specific passage
  const fetchMatchingRecordings = useCallback(async (book, chapter, start, end, filters = {}) => {
    if (!currentUser || userFamilyIds.length === 0) {
      return [];
    }

    try {
      const { selectedFamily = 'all', selectedReader = 'all', selectedPlan = 'all', availableListeningPlans = [] } = filters;
      
      // Get family member IDs
      const familyMemberIds = await getFamilyMemberIds(userFamilyIds);
      
      // Build queries for specific passage
      const queries = [];
      
      // User's recordings for this passage
      queries.push(
        query(
          collection(db, 'recordings'),
          where('userId', '==', currentUser.uid),
          where('book', '==', book),
          where('chapter', '==', chapter)
        )
      );
      
      // Family member recordings for this passage
      for (let i = 0; i < familyMemberIds.length; i += 10) {
        const batch = familyMemberIds.slice(i, i + 10);
        queries.push(
          query(
            collection(db, 'recordings'),
            where('userId', 'in', batch),
            where('book', '==', book),
            where('chapter', '==', chapter)
          )
        );
      }
      
      // Execute queries
      const snapshots = await Promise.all(queries.map(q => getDocs(q)));
      
      // Combine and de-duplicate (own/family queries overlap on the current user)
      const recordingsById = new Map();
      snapshots.forEach(snapshot =>
        snapshot.docs.forEach(doc => recordingsById.set(doc.id, { id: doc.id, ...doc.data() }))
      );
      let allRecordings = [...recordingsById.values()];

      // Filter by verse range and other filters
      const matches = allRecordings.filter(recording => {
        // Check verse range
        if (recording.verse < start || recording.verse > end) {
          return false;
        }
        
        // Apply family filter
        if (selectedFamily !== 'all' && (!recording.familyIds || !recording.familyIds.includes(selectedFamily))) {
          return false;
        }
        
        // Apply reader filter
        if (selectedReader !== 'all' && recording.userId !== selectedReader) {
          return false;
        }
        
        // Apply plan filter
        if (selectedPlan !== 'all') {
          const selectedPlanData = availableListeningPlans.find(p => p.id === selectedPlan);
          if (selectedPlanData && selectedPlanData.chapters) {
            const isInPlan = selectedPlanData.chapters.some(chapter => 
              chapter.book === recording.book && chapter.chapter === recording.chapter
            );
            if (!isInPlan) {
              return false;
            }
          }
        }
        
        return true;
      });
      
      return matches;
      
    } catch (error) {
      console.error('Error fetching matching recordings:', error);
      return [];
    }
  }, [currentUser, userFamilyIds, getFamilyMemberIds]);

  // Fetch all initial data
  const fetchAllData = useCallback(async () => {
    if (!currentUser || userFamilyIds.length === 0) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setData(prev => ({ ...prev, loading: true, error: null }));
      const fetchStart = performance.now();
      console.log(`⏱️ Listen load: fetchAllData starting ${Math.round(fetchStart - pageOpenedAt)}ms after page script loaded (families: ${userFamilyIds.length})`);

      // Fetch all data in parallel
      const [
        familiesResult,
        plansResult,
        listeningPlanResult,
        passagePlansResult,
        readersResult,
        recordingsResult,
        notesResult,
        favoritesResult
      ] = await Promise.allSettled([
        // Fetch family details
        timed('family docs', Promise.all(userFamilyIds.map(async (familyId) => {
          const familyDoc = await getDoc(doc(db, 'families', familyId));
          return familyDoc.exists() ? { id: familyId, ...familyDoc.data() } : null;
        })).then(families => families.filter(f => f !== null))),

        // Fetch plans available for listening = completed plans across all user's families,
        // hydrated with chapter data from the global plans collection
        timed('completed plans', (async () => {
          const seen = new Set();
          const completedPlanIds = [];
          for (const familyId of userFamilyIds) {
            const snap = await getDocs(collection(db, `families/${familyId}/completedPlans`));
            snap.docs.forEach(d => {
              const planId = d.data().planId || d.id;
              if (planId && !seen.has(planId)) {
                seen.add(planId);
                completedPlanIds.push(planId);
              }
            });
          }
          const planDocs = await Promise.all(
            completedPlanIds.map(id => getDoc(doc(db, 'plans', id)))
          );
          return planDocs
            .filter(d => d.exists())
            .map(d => ({ id: d.id, ...d.data() }));
        })()),

        // Fetch user's listening plan subscription
        timed('listening plan subscription', getDocs(collection(db, `users/${currentUser.uid}/listeningPlans`))
          .then(snapshot => snapshot.docs.length > 0 ? snapshot.docs[0].data() : null)
          .then(async (listeningPlan) => {
            if (listeningPlan) {
              const planDoc = await getDoc(doc(db, 'plans', listeningPlan.planId));
              return planDoc.exists() ? { id: listeningPlan.planId, ...planDoc.data() } : null;
            }
            return null;
          })),

        // Fetch subscribed Passages Plans that are actually listenable —
        // gated by hasAnyRecording rather than 100% completion (unlike
        // Reading Plans above), since Passages Plans are meant to stream as
        // you go.
        timed('passage plan subscriptions', (async () => {
          const subsSnap = await getDocs(collection(db, `users/${currentUser.uid}/passagePlanSubscriptions`));
          const activeSubs = subsSnap.docs.filter(d => d.data().status === 'active');
          if (activeSubs.length === 0) return [];

          const [planDocs, hasRecordingFlags] = await Promise.all([
            Promise.all(activeSubs.map(s => getDoc(doc(db, 'passagePlans', s.data().planId)))),
            Promise.all(activeSubs.map(async (s) => {
              const planId = s.data().planId;
              const progressDoc = await getDoc(doc(db, `users/${currentUser.uid}/passagePlanProgress`, planId));
              return progressDoc.exists();
            })),
          ]);

          return planDocs
            .map((d, i) => ({ d, hasAnyRecording: hasRecordingFlags[i] }))
            .filter(({ d, hasAnyRecording }) => d.exists() && hasAnyRecording)
            .map(({ d }) => ({ id: d.id, ...d.data() }));
        })()),

        // Fetch available readers — all member profiles in parallel
        timed('reader profiles', (async () => {
          const familyMemberIds = await getFamilyMemberIds(userFamilyIds);
          console.log(`⏱️ Listen load: reader profiles — fetching ${familyMemberIds.length} member docs in parallel`);
          const profileDocs = await Promise.all(
            familyMemberIds.map(memberId => getDoc(doc(db, 'users', memberId)))
          );
          return profileDocs
            .filter(userDoc => userDoc.exists())
            .map(userDoc => {
              const userData = userDoc.data();
              return {
                id: userDoc.id,
                name: userData.displayName || userData.email || 'Unknown User',
                email: userData.email
              };
            });
        })()),

        // Fetch recordings
        timed('all recordings', fetchRecordings()),

        // Fetch notes for current chapter (will be updated when chapter changes)
        Promise.resolve({ verseNotes: {}, hasNotesForChapter: false }),

        // Fetch favorites
        timed('favorited verses', getDocs(collection(db, `users/${currentUser.uid}/favoritedVerses`))
          .then(snapshot => {
            const favorites = new Set();
            snapshot.docs.forEach(doc => {
              const data = doc.data();
              const verseKey = `${data.book}_${data.chapter}_${data.verse}`;
              favorites.add(verseKey);
            });
            return favorites;
          }))
      ]);
      console.log(`⏱️ Listen load: fetchAllData total ${Math.round(performance.now() - fetchStart)}ms`);

      // Process results
      const userFamilies = familiesResult.status === 'fulfilled' ? familiesResult.value : [];
      const availableListeningPlans = plansResult.status === 'fulfilled' ? plansResult.value : [];
      const currentListeningPlan = listeningPlanResult.status === 'fulfilled' ? listeningPlanResult.value : null;
      const availablePassagePlans = passagePlansResult.status === 'fulfilled' ? passagePlansResult.value : [];
      const availableReaders = readersResult.status === 'fulfilled' ? readersResult.value : [];
      const recordingsData = recordingsResult.status === 'fulfilled' ? recordingsResult.value : { recordedPassages: [], matchingRecordings: [], allRecordings: [] };
      const notesData = notesResult.status === 'fulfilled' ? notesResult.value : { verseNotes: {}, hasNotesForChapter: false };
      const favoritedVerses = favoritesResult.status === 'fulfilled' ? favoritesResult.value : new Set();

      console.log('📊 Data fetch results:', {
        userFamilies: userFamilies.length,
        availableListeningPlans: availableListeningPlans.length,
        availableReaders: availableReaders.length,
        recordingsData: {
          recordedPassages: recordingsData.recordedPassages?.length || 0,
          matchingRecordings: recordingsData.matchingRecordings?.length || 0,
          allRecordings: recordingsData.allRecordings?.length || 0,
          sampleRecordings: recordingsData.allRecordings?.slice(0, 2)
        },
        errors: {
          families: familiesResult.status === 'rejected' ? familiesResult.reason : null,
          plans: plansResult.status === 'rejected' ? plansResult.reason : null,
          recordings: recordingsResult.status === 'rejected' ? recordingsResult.reason : null
        }
      });

      setData(prev => ({
        ...prev,
        userFamilies,
        availableListeningPlans,
        currentListeningPlan,
        availablePassagePlans,
        availableReaders,
        recordedPassages: recordingsData.recordedPassages,
        matchingRecordings: recordingsData.matchingRecordings,
        allRecordings: recordingsData.allRecordings,
        verseNotes: notesData.verseNotes,
        hasNotesForChapter: notesData.hasNotesForChapter,
        hasSearchedForRecordings: true,
        favoritedVerses,
        loading: false,
      }));

    } catch (error) {
      console.error('Error fetching all data:', error);
      setData(prev => ({ ...prev, loading: false, error: error.message }));
    }
  }, [currentUser, userFamilyIds, getFamilyMemberIds, fetchRecordings]);

  // Memoized selectors for derived state
  const selectors = useMemo(() => ({
    // Filtered passages based on search
    getFilteredPassages: (search) => {
      if (!search) return data.recordedPassages;
      return data.recordedPassages.filter(p =>
        p.passage.toLowerCase().includes(search.toLowerCase())
      );
    },
    
    // Get readers for selected family
    getReadersForFamily: (selectedFamily) => {
      if (selectedFamily === 'all') return data.availableReaders;
      // Filter readers by family (this would need additional data structure)
      return data.availableReaders;
    },
    
    // Check if verse has recording
    hasRecording: (book, chapter, verse) => {
      return data.matchingRecordings.some(rec => 
        rec.book === book && rec.chapter === chapter && rec.verse === verse
      );
    },
    
    // Get recordings for specific passage
    getRecordingsForPassage: (book, chapter, start, end) => {
      // Use the allRecordings data which contains the full recording objects
      const allRecordings = data.allRecordings || [];
      
      // Filter by passage criteria and verse range
      return allRecordings.filter(rec => 
        rec.book === book && 
        rec.chapter === chapter && 
        rec.verse >= start && 
        rec.verse <= end
      );
    }
  }), [data]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    ...data,
    selectors,
    fetchRecordings,
    fetchMatchingRecordings,
    refetch: fetchAllData,
  };
}
